import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import {
  getInitialRealtimeListenLimit,
  selectRealtimeMeasurementDocs,
} from "./loadStagedMeasurementDocs";
import { mapMeasurementDocToRow } from "./mapMeasurementRow";
import { mapSpirometryDocToRow } from "./mapSpirometryDocToRow";
import { SPIROMETRY_PREDICTION_SOURCES } from "../../utils/results/spirometry";

const RESULTS_METADATA_CACHE_TTL_MS = 5 * 60 * 1000;

const prefetchedResultsMetadataCache = new Map();

function cloneRows(rows) {
  return Array.isArray(rows)
    ? rows.map((row) => ({ ...row }))
    : [];
}

export function buildResultsMetadataCacheKey({
  uid,
  collectionName,
  context = null,
  orderField = "documentId",
  orderDirection = "desc",
  language = "en-GB",
  kind = "generic",
  predictionSource = null,
}) {
  return [
    kind,
    uid || "",
    collectionName || "",
    context || "",
    orderField,
    orderDirection,
    language,
    predictionSource || "",
  ].join("::");
}

export function readPrefetchedResultsMetadata(
  cacheKey,
  now = Date.now(),
) {
  const entry = prefetchedResultsMetadataCache.get(cacheKey);

  if (!entry) {
    return null;
  }

  if (now - entry.cachedAt > RESULTS_METADATA_CACHE_TTL_MS) {
    prefetchedResultsMetadataCache.delete(cacheKey);
    return null;
  }

  return {
    rows: cloneRows(entry.rows),
    hasMore: entry.hasMore,
  };
}

export function writePrefetchedResultsMetadata(
  cacheKey,
  { rows = [], hasMore = false },
  now = Date.now(),
) {
  prefetchedResultsMetadataCache.set(cacheKey, {
    rows: cloneRows(rows),
    hasMore: Boolean(hasMore),
    cachedAt: now,
  });
}

export function clearPrefetchedResultsMetadata() {
  prefetchedResultsMetadataCache.clear();
}

function buildGenericResultsQuery({
  colRef,
  context = null,
  orderField = "documentId",
  orderDirection = "desc",
  listenLimit,
}) {
  const constraints = [];

  if (context) {
    constraints.push(where("appMode", "==", context));
  }

  if (orderField === "documentId") {
    constraints.push(orderBy(documentId(), orderDirection));
  } else {
    constraints.push(orderBy(orderField, orderDirection));
  }

  return query(colRef, ...constraints, limit(listenLimit));
}

export async function prefetchMeasurementResultsMetadata({
  db,
  uid,
  collectionName,
  context = null,
  pageSize = 20,
  orderField = "documentId",
  orderDirection = "desc",
  language = "en-GB",
}) {
  if (!db || !uid || !collectionName) {
    return null;
  }

  const colRef = collection(db, "measurements", uid, collectionName);
  const listenLimit = getInitialRealtimeListenLimit(pageSize, false);
  const snap = await getDocs(
    buildGenericResultsQuery({
      colRef,
      context,
      orderField,
      orderDirection,
      listenLimit,
    }),
  );
  const docs = selectRealtimeMeasurementDocs(snap.docs, pageSize);
  const rows = docs.map((doc) =>
    mapMeasurementDocToRow(doc, context, language),
  );
  const hasMore = snap.size >= listenLimit;
  const cacheKey = buildResultsMetadataCacheKey({
    uid,
    collectionName,
    context,
    orderField,
    orderDirection,
    language,
  });

  writePrefetchedResultsMetadata(cacheKey, {
    rows,
    hasMore,
  });

  return {
    rows,
    hasMore,
  };
}

export async function prefetchSpirometryResultsMetadata({
  db,
  uid,
  pageSize = 20,
  language = "en-GB",
  predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
}) {
  if (!db || !uid) {
    return null;
  }

  const colRef = collection(db, "measurements", uid, "RecordingSpirometry");
  const listenLimit = getInitialRealtimeListenLimit(pageSize, false);
  const snap = await getDocs(
    query(colRef, orderBy(documentId(), "desc"), limit(listenLimit)),
  );
  const docs = selectRealtimeMeasurementDocs(snap.docs, pageSize);
  const rows = docs.map((doc) =>
    mapSpirometryDocToRow(doc, language, {
      predictionSource,
    }),
  );
  const hasMore = snap.size >= listenLimit;
  const cacheKey = buildResultsMetadataCacheKey({
    uid,
    collectionName: "RecordingSpirometry",
    orderField: "documentId",
    orderDirection: "desc",
    language,
    kind: "spirometry",
    predictionSource,
  });

  writePrefetchedResultsMetadata(cacheKey, {
    rows,
    hasMore,
  });

  return {
    rows,
    hasMore,
  };
}
